/**
 * Theming system
 *
 * Themes are applied by setting a `data-theme` attribute on `<html>`.
 * All colour tokens live in CSS (`index.css`) under `[data-theme="..."]`
 * selectors — JavaScript never touches individual CSS properties.
 *
 * "light"    — default light theme
 * "midnight" — dark theme
 * "system"   — resolved in JS: "light" or "midnight" based on OS preference
 *
 * To add a new theme later:
 *   1. Add its id to the `ThemeId` union below.
 *   2. Add a `Theme` entry to `THEMES`.
 *   3. Add a `[data-theme="your-id"] { ... }` block in `index.css`.
 */

/** Extend this union as new themes are introduced. */
export type ThemeId = "light" | "midnight" | "system";

export interface Theme {
  id: ThemeId;
  /** Human-readable label */
  label: string;
  /** Whether this is a dark-mode theme (informs `color-scheme` meta). */
  dark: boolean;
  /** Representative accent colour for swatch previews. */
  accent: string;
}

export const THEMES: Theme[] = [
  {
    id: "light",
    label: "Light",
    dark: false,
    accent: "oklch(0.55 0.18 290)",
  },
  {
    id: "midnight",
    label: "Dark",
    dark: true,
    accent: "oklch(0.72 0.15 290)",
  },
  {
    id: "system",
    label: "System",
    dark: false, // placeholder; resolved at runtime
    accent: "oklch(0.62 0.15 290)",
  },
];

export const DEFAULT_THEME: ThemeId = "midnight";
const STORAGE_KEY = "scoracle-theme";

/**
 * Returns the validated theme id from localStorage, falling back to the
 * default.
 */
export function loadStoredTheme(): ThemeId {
  // Theme selection is disabled — always use the default.
  // To re-enable: uncomment the two lines below and remove the early return.
  // const stored = localStorage.getItem(STORAGE_KEY);
  // if (THEMES.some((t) => t.id === stored)) return stored as ThemeId;
  return DEFAULT_THEME;
}

/**
 * Resolves a ThemeId to the actual CSS theme to apply.
 * "system" resolves to "light" or "midnight" based on OS preference.
 */
export function resolveTheme(id: ThemeId): "light" | "midnight" {
  if (id === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "midnight"
      : "light";
  }
  return id;
}

/**
 * Applies a theme by setting `data-theme` on `<html>` and persisting the
 * choice. The actual CSS variables live in `index.css` — this function
 * never mutates inline styles.
 *
 * For "system", resolves to the OS-preferred theme before applying.
 */
export function applyTheme(id: ThemeId): void {
  const resolved = resolveTheme(id);
  document.documentElement.setAttribute("data-theme", resolved);

  // Keep `color-scheme` meta in sync for OS scroll-bars / form controls.
  let meta = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "color-scheme";
    document.head.appendChild(meta);
  }
  meta.content = resolved === "midnight" ? "dark" : "light";

  localStorage.setItem(STORAGE_KEY, id);
}

/**
 * Maps a DB ThemePreference value ("LIGHT" | "DARK" | "SYSTEM") to a ThemeId.
 */
export function dbThemeToThemeId(dbTheme: "LIGHT" | "DARK" | "SYSTEM"): ThemeId {
  switch (dbTheme) {
    case "LIGHT":  return "light";
    case "DARK":   return "midnight";
    case "SYSTEM": return "system";
  }
}

/**
 * Maps a ThemeId to the DB ThemePreference value.
 */
export function themeIdToDbTheme(id: ThemeId): "LIGHT" | "DARK" | "SYSTEM" {
  switch (id) {
    case "light":    return "LIGHT";
    case "midnight": return "DARK";
    case "system":   return "SYSTEM";
  }
}
